import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { MetricCard } from '../components/MetricCard';
import { StatusBadge } from '../components/StatusBadge';
import { VisualTruckOwnership, VisualTrailerOwnership } from '../components/VisualOwnership';
import { portfolioAPI, requestsAPI } from '../lib/api';
import { formatCurrency, formatNumber, formatPercentage, formatDateTime } from '../lib/formatters';
import { DollarSign, TrendingUp, Clock, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const [portfolioResponse, requestsResponse] = await Promise.all([
        portfolioAPI.get(),
        requestsAPI.list(),
      ]);
      setPortfolio(portfolioResponse.data);
      setRequests(requestsResponse.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-white border border-[hsl(var(--border))] rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            My Portfolio
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Track your investments and share ownership
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <MetricCard
            title="Total Shares Owned"
            value={formatNumber(portfolio?.summary?.total_shares_owned || 0)}
            subtitle="Across all assets"
            icon={Briefcase}
            color="neutral"
          />
          <MetricCard
            title="Portfolio Value"
            value={formatCurrency(portfolio?.summary?.portfolio_value || 0)}
            subtitle="Current market value"
            icon={DollarSign}
            color="primary"
          />
          <MetricCard
            title="Total Invested"
            value={formatCurrency(portfolio?.summary?.total_invested || 0)}
            subtitle="Purchase value"
            icon={TrendingUp}
            color="gain"
          />
          <MetricCard
            title="Pending Requests"
            value={formatNumber(portfolio?.summary?.pending_requests || 0)}
            subtitle="Awaiting approval"
            icon={Clock}
            color="neutral"
          />
        </div>

        {/* Owned Assets */}
        {portfolio?.assets && portfolio.assets.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-[hsl(var(--text-primary))] mb-4">
              My Owned Assets
            </h2>
            
            {/* Visual Ownership Cards */}
            <div className="space-y-6 mb-8">
              {portfolio.assets.map(({ asset, shares_owned, ownership_percentage, current_value }) => (
                <div key={asset.asset_id}>
                  {asset.type === 'truck' ? (
                    <VisualTruckOwnership ownershipPercentage={ownership_percentage} />
                  ) : (
                    <VisualTrailerOwnership ownershipPercentage={ownership_percentage} />
                  )}
                  <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6 mt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-[hsl(var(--text-primary))] mb-1">
                          {asset.name}
                        </h3>
                        <p className="text-sm text-[hsl(var(--text-secondary))] font-mono">
                          Unit #{asset.unit_number}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate(`/asset/${asset.asset_id}`)}
                        className="px-4 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                      >
                        View Details
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
                        <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Shares Owned</div>
                        <div className="text-2xl font-bold font-mono text-[hsl(var(--brand-primary))]">
                          {shares_owned}
                        </div>
                      </div>
                      <div className="p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
                        <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Ownership</div>
                        <div className="text-2xl font-bold font-mono text-[hsl(var(--gain))]">
                          {formatPercentage(ownership_percentage)}
                        </div>
                      </div>
                      <div className="p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
                        <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Current Value</div>
                        <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">
                          {formatCurrency(current_value)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request History */}
        <div>
          <h2 className="text-2xl font-semibold text-[hsl(var(--text-primary))] mb-4">
            Request History
          </h2>
          {requests.length === 0 ? (
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
              <p className="text-[hsl(var(--text-secondary))]">No requests yet</p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 px-6 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Browse Fleet
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid="requests-history-table" className="w-full">
                  <thead className="bg-[hsl(var(--background-secondary))]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase tracking-wider">
                        Asset
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase tracking-wider">
                        Shares
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--border))]">
                    {requests.map((request) => (
                      <tr
                        key={request.request_id}
                        data-testid={`request-row-${request.request_id}`}
                        className="hover:bg-[hsl(var(--background-secondary))] transition-colors duration-150"
                      >
                        <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">
                          {formatDateTime(request.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[hsl(var(--text-primary))]">
                          <button
                            onClick={() => navigate(`/asset/${request.asset_id}`)}
                            className="text-[hsl(var(--brand-primary))] hover:underline"
                          >
                            View Asset
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm font-mono text-[hsl(var(--text-primary))]">
                          {formatNumber(request.shares_requested)}
                        </td>
                        <td className="px-6 py-4 text-sm font-mono font-semibold text-[hsl(var(--text-primary))]">
                          {formatCurrency(request.total_amount)}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={request.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
